import React from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { serviceSchema } from '../lib/schema';
import { PageMeta, PageHero, SectionHead, CtaBand, Icon, Price, AppButton } from '../components/ui';

const FEATURES = [
  ['Exterior hand wash', ['basic', 'deluxe', 'premium']],
  ['Wheel cleaning', ['basic', 'deluxe', 'premium']],
  ['Exterior window cleaning', ['basic', 'deluxe', 'premium']],
  ['Tyre dressing', ['basic', 'deluxe', 'premium']],
  ['Interior vacuum', ['deluxe', 'premium']],
  ['Dashboard wipe', ['deluxe', 'premium']],
  ['Door jamb cleaning', ['deluxe', 'premium']],
  ['Air freshener', ['deluxe', 'premium']],
  ['Leather conditioning', ['premium']],
  ['Engine bay cleaning', ['premium']],
  ['Clay bar treatment', ['premium']],
  ['Wax protection', ['premium']]
];

const Pricing = () => {
  const { PACKAGES, ADDONS, PLANS } = useSite();
  return (
  <>
    <PageMeta
      title="Car Wash Prices UAE | Mobile Car Wash from AED 30 | Quick Way"
      description="Mobile car wash prices in Abu Dhabi and Dubai: Basic Wash AED 30, Deluxe Wash AED 45, Premium Detail AED 65. Compare inclusions, extras and monthly plans."
      path="/pricing"
      crumbs={[{ name: 'Pricing', path: '/pricing' }]}
      jsonLd={[serviceSchema({ name: 'Mobile car wash', description: 'Mobile car wash packages in Abu Dhabi and Dubai.', path: '/pricing', offers: PACKAGES })]}
    />
    <PageHero eyebrow="Pricing" title="Mobile car wash prices">
      <p>Three packages, a short list of extras and optional monthly plans. Every price below is for a wash at your location in Abu Dhabi or Dubai.</p>
    </PageHero>

    <section className="section">
      <div className="container">
        <SectionHead title="Compare car wash packages" />
        <div className="table-wrap" role="region" aria-label="Package comparison" tabIndex="0">
          <table className="compare">
            <caption className="sr-only">What each Quick Way package includes</caption>
            <thead>
              <tr>
                <th scope="col">Included</th>
                {PACKAGES.map((p) => (
                  <th key={p.id} scope="col" className={p.featured ? 'is-featured' : ''}>
                    <span className="compare__name">{p.name}</span>
                    <Price value={p.price} />
                    <span className="compare__dur">{p.duration}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map(([label, ids]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  {PACKAGES.map((p) => (
                    <td key={p.id} className={p.featured ? 'is-featured' : ''}>
                      {ids.includes(p.id)
                        ? <><Icon name="check" size={18} className="yes" /><span className="sr-only">Included</span></>
                        : <><span className="no" aria-hidden="true">–</span><span className="sr-only">Not included</span></>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <th scope="row"><span className="sr-only">Book</span></th>
                {PACKAGES.map((p) => (
                  <td key={p.id} className={p.featured ? 'is-featured' : ''}>
                    <AppButton label="Book in app" className={`btn btn-sm ${p.featured ? 'btn-primary' : 'btn-outline-dark'}`}
                      source="pricing" params={{ package: p.id }} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="note-box">
          <Icon name="tag" size={20} />
          <p>
            Prices are standard package prices in UAE dirhams. For large SUVs, vans or heavily soiled vehicles,
            we confirm the final price, including any applicable VAT, before the wash starts.
          </p>
        </div>
      </div>
    </section>

    <section className="section section--tint">
      <div className="container">
        <SectionHead title="Extras" />
        <div className="table-wrap">
          <table className="simple-table">
            <thead><tr><th scope="col">Extra</th><th scope="col">What it does</th><th scope="col">Price</th></tr></thead>
            <tbody>
              {ADDONS.map((a) => (
                <tr key={a.id}><th scope="row">{a.name}</th><td>{a.desc}</td><td><Price value={a.price} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <SectionHead title="Monthly car wash plans">
          Regular washes with priority booking. <Link to="/membership">See full plan details</Link>.
        </SectionHead>
        <div className="table-wrap">
          <table className="simple-table">
            <thead><tr><th scope="col">Plan</th><th scope="col">Includes</th><th scope="col">Value</th><th scope="col">Monthly price</th></tr></thead>
            <tbody>
              {PLANS.map((p) => (
                <tr key={p.id}><th scope="row">{p.name}</th><td>{p.washes}</td><td>{p.perWash}</td><td><Price value={p.price} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <CtaBand source="pricing" />
  </>
);
};

export default Pricing;
